import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sun,
  Moon,
  CalendarDays,
  Sparkles,
  RefreshCw,
  Clock,
  Lightbulb,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import HackCard from '@/components/HackCard';
import ProductDetailDialog from '@/components/ProductDetailDialog';
import PaywallDialog from '@/components/PaywallDialog';
import { PromoBadge } from '@/components/PromoBanner';

const StepRow = ({ step, onProductOpen }) => {
  const product = step.product;
  return (
    <Card className="rounded-2xl bg-card border-border/70 p-5" data-testid="regimen-step">
      <div className="flex items-start gap-4">
        <div className="shrink-0 h-10 w-10 rounded-full bg-accent text-rose-gold flex items-center justify-center font-sans text-sm font-semibold border border-rose-tint">
          {step.order ?? '•'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-display text-xl text-ink leading-tight">{step.step || step.ritual}</h4>
            {step.day && (
              <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em] font-medium">
                {step.day}
              </Badge>
            )}
            {step.minutes != null && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> {step.minutes} min
              </span>
            )}
            {step.category && (
              <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em] border-border">
                {step.category}
              </Badge>
            )}
          </div>
          {step.description && (
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">{step.description}</p>
          )}
          {step.tip && (
            <p className="mt-2 text-xs text-muted-foreground flex gap-1.5 items-start">
              <Lightbulb className="h-3.5 w-3.5 text-rose-gold mt-0.5 shrink-0" />
              <span>{step.tip}</span>
            </p>
          )}
          {product && (
            <button
              type="button"
              onClick={() => onProductOpen?.(product)}
              className="mt-3 w-full text-left rounded-xl border border-border/70 bg-cream-paper p-3 hover:bg-secondary/60 transition-colors flex items-center gap-3"
              data-testid={`regimen-product-${product.id}`}
            >
              <div className="h-12 w-12 rounded-md overflow-hidden bg-cream shrink-0">
                <img src={product.image_url} alt={product.name} className="h-full w-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {product.brand} · {product.tier === 'premium' ? 'Premium Luxe' : 'Essential Pick'}
                </p>
                <p className="text-sm font-medium truncate">{product.name}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default function RegimenPage() {
  const { user, setCreditsLocal, credits, promo } = useAuth();
  const navigate = useNavigate();
  const [regimen, setRegimen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [openProduct, setOpenProduct] = useState(null);
  const [paywall, setPaywall] = useState({ open: false, required: 2, balance: 0 });

  const profileComplete = !!user?.skin_type && !!user?.lifestyle;

  const loadExisting = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/regimen/me');
      setRegimen(data?.regimen || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) loadExisting();
  }, [user]);

  const generate = async () => {
    if (!profileComplete) {
      toast('Set your skin type and lifestyle first', {
        description: 'A few quick answers help us personalize your routine.',
        action: { label: 'Open profile', onClick: () => navigate('/profile') },
      });
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post('/regimen/generate');
      setRegimen(data.regimen);
      if (typeof data?.credits === 'number') setCreditsLocal(data.credits);
      toast.success('Your personalized regimen is ready');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail?.error === 'insufficient_credits') {
        setPaywall({
          open: true,
          required: detail.required ?? 2,
          balance: detail.balance ?? 0,
        });
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Couldn\u2019t generate regimen. Try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="regimen-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
          <CalendarDays className="h-3.5 w-3.5" /> Your beauty regimen
        </span>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
          Your daily &amp; weekly ritual.
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          A personalized routine generated from your skin type, concerns, region and lifestyle. Each step is paired with a real product
          you can actually buy where you live.
        </p>
        <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
          {promo?.active ? (
            <PromoBadge />
          ) : (
            <>
              <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
                2 credits per generation
              </Badge>
              <span className="text-muted-foreground tabular-nums">
                · Balance: <span className="font-semibold text-foreground">{credits}</span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Profile snapshot + generate CTA */}
      <Card className="mt-8 bg-card rounded-2xl border-border/70 p-5 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
              Skin: {user.skin_type || 'not set'}
            </Badge>
            <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
              Region: {user.region || 'global'}
            </Badge>
            <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
              Lifestyle: {user.lifestyle || 'not set'}
            </Badge>
            <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
              Time: {user.time_budget || '10min'}
            </Badge>
            <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
              Budget: {user.budget_pref || 'mix'}
            </Badge>
            <Badge className="bg-secondary text-foreground border border-border text-[10px] uppercase tracking-[0.14em]">
              Glamour: {user.glamour_level || 'natural'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => navigate('/profile')}
              data-testid="regimen-edit-profile-button"
            >
              Edit answers
            </Button>
            <Button
              onClick={generate}
              disabled={generating}
              className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="regimen-generate-button"
            >
              {generating ? (
                <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Generating...</>
              ) : regimen ? (
                <><Sparkles className="h-4 w-4 mr-1" /> Regenerate</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-1" /> Generate my regimen</>
              )}
            </Button>
          </div>
        </div>
        {!profileComplete && (
          <p className="mt-3 text-xs text-muted-foreground">
            Tip: complete your skin type and lifestyle in the{' '}
            <Link to="/profile" className="underline underline-offset-4 text-foreground">profile</Link> for the most accurate routine.
          </p>
        )}
      </Card>

      {loading ? (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      ) : !regimen ? (
        <Card className="mt-8 rounded-2xl bg-cream-paper border-border/70 p-10 text-center">
          <Sparkles className="h-9 w-9 mx-auto text-rose-gold" />
          <p className="mt-3 font-display text-2xl text-ink">No regimen yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Click <em className="not-italic font-medium">Generate my regimen</em> above and we&rsquo;ll craft a personalised AM, PM and weekly routine in seconds.
          </p>
        </Card>
      ) : (
        <div className="mt-8" data-testid="regimen-content">
          {regimen.headline && (
            <h2 className="font-display text-2xl sm:text-3xl text-ink leading-tight">{regimen.headline}</h2>
          )}
          {regimen.summary && (
            <p className="mt-2 text-muted-foreground leading-relaxed max-w-3xl">{regimen.summary}</p>
          )}

          <Tabs defaultValue="am" className="mt-6">
            <TabsList className="bg-secondary rounded-xl p-1 border border-border/70 grid grid-cols-3 max-w-md">
              <TabsTrigger value="am" className="rounded-lg data-[state=active]:bg-card" data-testid="regimen-tab-am">
                <Sun className="h-3.5 w-3.5 mr-1.5" /> Daily AM
              </TabsTrigger>
              <TabsTrigger value="pm" className="rounded-lg data-[state=active]:bg-card" data-testid="regimen-tab-pm">
                <Moon className="h-3.5 w-3.5 mr-1.5" /> Daily PM
              </TabsTrigger>
              <TabsTrigger value="weekly" className="rounded-lg data-[state=active]:bg-card" data-testid="regimen-tab-weekly">
                <CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Weekly
              </TabsTrigger>
            </TabsList>

            <TabsContent value="am" className="mt-6 space-y-4">
              {(regimen.daily_am || []).map((s, i) => (
                <StepRow key={`am-${i}`} step={s} onProductOpen={setOpenProduct} />
              ))}
              {!(regimen.daily_am || []).length && <Empty label="No AM steps" />}
            </TabsContent>
            <TabsContent value="pm" className="mt-6 space-y-4">
              {(regimen.daily_pm || []).map((s, i) => (
                <StepRow key={`pm-${i}`} step={s} onProductOpen={setOpenProduct} />
              ))}
              {!(regimen.daily_pm || []).length && <Empty label="No PM steps" />}
            </TabsContent>
            <TabsContent value="weekly" className="mt-6 space-y-4">
              {(regimen.weekly || []).map((s, i) => (
                <StepRow key={`wk-${i}`} step={s} onProductOpen={setOpenProduct} />
              ))}
              {!(regimen.weekly || []).length && <Empty label="No weekly rituals" />}
            </TabsContent>
          </Tabs>

          {/* Wellness + Watchouts */}
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {(regimen.wellness || []).length > 0 && (
              <Card className="rounded-2xl bg-cream-paper border-border/70 p-6">
                <h3 className="font-display text-xl text-ink flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-rose-gold" /> Wellness habits
                </h3>
                <ul className="mt-3 space-y-2">
                  {regimen.wellness.map((w, i) => (
                    <li key={i} className="text-sm text-foreground/85 flex gap-2 items-start">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-rose-gold shrink-0" />
                      {w}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
            {(regimen.watchouts || []).length > 0 && (
              <Card className="rounded-2xl bg-accent/40 border border-rose-tint/70 p-6">
                <h3 className="font-display text-xl text-ink flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-rose-gold" /> Gentle watchouts
                </h3>
                <ul className="mt-3 space-y-2">
                  {regimen.watchouts.map((w, i) => (
                    <li key={i} className="text-sm text-foreground/85 flex gap-2 items-start">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-rose-gold shrink-0" />
                      {w}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {/* DIY pairings */}
          {(regimen.diy_hacks || []).length > 0 && (
            <section className="mt-10">
              <h3 className="font-display text-2xl text-ink mb-4">DIY rituals to pair</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {regimen.diy_hacks.map((h) => (
                  <HackCard key={h.id} hack={h} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <ProductDetailDialog
        product={openProduct}
        open={!!openProduct}
        onOpenChange={(o) => !o && setOpenProduct(null)}
      />
    </div>
  );
}

const Empty = ({ label }) => (
  <Card className="rounded-2xl bg-card border-border/70 p-6 text-center text-sm text-muted-foreground">
    {label}
  </Card>
);
